import getStates from '@/assets/js/shared/helpers/getStatesList';

// Build one set of valid US state names and abbreviations to reference on module load
const states = getStates({ hasTerritories: true });
const validStates = new Set([
  ...Object.keys(states).map(abbreviation => abbreviation.toLowerCase()),
  ...Object.values(states).map(stateName =>
    stateName.replace(/ /g, '-').toLowerCase()
  )
]);

/**
 * True when the route params object includes the key, even if the value is empty
 *
 * @param {object} params
 * @param {string} key
 * @returns {boolean}
 */
function hasParam(params, key) {
  return Object.prototype.hasOwnProperty.call(params, key);
}

/**
 * True when the value is the string "null"
 *
 * @param {*} value
 * @returns {boolean}
 */
function isNullString(value) {
  return typeof value === 'string' && value.toLowerCase() === 'null';
}

/**
 * Returns `page` when that key is present and the value is null or not a whole number
 *
 * @param {object} params
 * @returns {string[]}
 */
function getInvalidPageKeys(params) {
  if (!hasParam(params, 'page') || params.page === undefined) {
    return [];
  }

  const page = params.page;
  const isNumericPage = page !== null && /^\d+$/.test(String(page));

  return isNumericPage ? [] : ['page'];
}

/**
 * Returns `city` and/or `state` when those keys are present and the value is the string "null"
 * A null city or state also drops `page`, because page is always the last segment
 * An invalid state also drops `city` and `page`
 *
 * @param {object} params
 * @returns {string[]}
 */
function getNullCityOrStateKeys(params) {
  const hasState = hasParam(params, 'state');
  const hasCity = hasParam(params, 'city');
  const hasPage = hasParam(params, 'page');

  if (!hasState && !hasCity) {
    return [];
  }
  let keys = [];
  const stateIsNullOrInvalid =
    hasState && (isNullString(params.state) || !isValidState(params.state));
  const cityIsNull = hasCity && isNullString(params.city);
  const dropPage = () => {
    if (
      !keys.includes('page') &&
      hasPage &&
      params.page != null &&
      params.page !== ''
    ) {
      keys.push('page');
    }
  };

  if (stateIsNullOrInvalid) {
    keys.push('state');
    if (hasCity) {
      keys.push('city');
    }
    dropPage();
    return keys;
  }

  if (cityIsNull) {
    keys.push('city');
    dropPage();
  }

  return keys;
}

/**
 * Returns true when the state is a valid US state
 *
 * @param {string} state
 * @returns {boolean}
 */
function isValidState(state) {
  // Guard against optional state param
  if (!state) {
    return true;
  }

  return validStates.has(String(state).toLowerCase());
}

/**
 * Removes path segments whose value matches the given param keys
 *
 * @param {object} route
 * @param {string[]} keys
 * @returns {string}
 */
function pathWithoutParams(route, keys) {
  let path = route.path || '/';

  keys.forEach(key => {
    const value = route.params[key];
    if (value == null || value === '') {
      return;
    }

    // Escape regex metacharacters in the param so they match literally
    const segment = String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    path = path.replace(new RegExp(`/${segment}(?=/|$)`, 'ig'), '');
  });

  // Collapse slashes left behind after a segment is removed, e.g. "//" -> "/"
  path = path.replace(/\/{2,}/g, '/');

  if (!path || path === '/') {
    return '/';
  }

  return path;
}

/**
 * Redirects when `page` is not numeric, or when `city` / `state` is the string "null" or State is invalid
 *
 * @param {object} context
 * @param {object} context.route
 * @param {Function} context.redirect
 */
export default function({ route, redirect }) {
  // Exclude dashboard routes from invalid param checking
  if (route.path?.includes('/dashboard/')) {
    return;
  }

  const params = route.params || {};

  // Get the route parameter keys that are invalid
  const keys = [
    ...getInvalidPageKeys(params),
    ...getNullCityOrStateKeys(params)
  ];

  if (!keys.length) {
    return;
  }

  // Remove the invalid keys from the path
  const path = pathWithoutParams(route, keys);

  if (path === route.path) {
    return;
  }

  return redirect({
    path,
    query: route.query
  });
}
